import React, { type FC, type KeyboardEvent, useMemo, useCallback, useRef, useEffect } from 'react';

import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import { Box, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { AIContainer, AiIcon as AILogo, AiFooter } from '@atlassian/jira-atlassian-intelligence';
import type { ProjectType } from '@atlassian/jira-common-constants/src/project-types';
import { ErrorBoundary } from '@atlassian/jira-error-boundary';
import { ff } from '@atlassian/jira-feature-flagging';
import { useIntl } from '@atlassian/jira-intl';
import JiraIssueLineCard from '@atlassian/jira-issue-line-card';
import { IssueBreakdownLineSkeleton } from '@atlassian/jira-skeletons/src/ui/issue-breakdown';
import { NavigationBlocker } from '@atlassian/routing-utils';
import type { ChildIssueType } from '../../../../model/types';
import type { DefaultValue as DefaultPriority } from '../../../../services/use-default-priority/types';
import type { DefaultValue as DefaultStatus } from '../../../../services/use-default-status/types';
import { useIssueBreakdown, type SuggestedChildIssue } from '../../context';
import messages from '../../messages';
import { getSelectedIssueType } from '../../utils';
import { FeedbackForm } from '../common/feedback-form';
import { Footer } from '../common/footer';
import { Header } from '../common/header';
import { InputPrompt } from '../common/input-prompt';
import { DiscardSuggestions } from './discard-suggestions';
// eslint-disable-next-line jira/tangerine-view-messages-export-import
import customModalMessages from './messages';
import { SubscribeToIssueBreakdown } from './subscribe-to-issue-breakdown';
import { getLoadingStatusMessage, getNextItemIndex } from './utils';

type Props = {
	onSubmit(): void;
	onDiscard(): void;
	onChangePrompt(value: string): void;
	onClickItem(item: SuggestedChildIssue): void;
	onFocusPrompt(): void;
	onStreamComplete?(): void;
	isLoading: boolean;
	projectType: ProjectType | null;
	defaultStatus?: DefaultStatus;
	defaultPriority?: DefaultPriority;
	childIssueTypes: ChildIssueType[];
	items?: SuggestedChildIssue[];
	issueHierarchyLevel?: number | undefined;
};

export const IssueBreakdownDraftList: FC<Props> = ({
	onSubmit,
	onDiscard,
	onClickItem,
	onChangePrompt,
	onFocusPrompt,
	onStreamComplete,
	isLoading,
	items = [],
	projectType,
	defaultStatus,
	defaultPriority,
	childIssueTypes,
	issueHierarchyLevel,
}) => {
	const { formatMessage } = useIntl();
	const [
		{ selectedIssueIndex, headerPromptValue, isStreaming, status },
		{ setSelectedIssueIndex, updateStreamingStatus, openDiscardSuggestionsModal },
	] = useIssueBreakdown();

	const handleOnStop = useCallback(() => {
		if (items.length > 0) {
			updateStreamingStatus(false);
			onStreamComplete?.();
		} else {
			onDiscard();
		}
	}, [items.length, onDiscard, onStreamComplete, updateStreamingStatus]);

	const handleKeyDown = useCallback(
		(event: KeyboardEvent<HTMLInputElement>) => {
			if (event.key === 'Escape') {
				event.preventDefault();
				event.stopPropagation(); // Prevents issue modal from closing
				if (isStreaming) {
					handleOnStop();
				} else {
					openDiscardSuggestionsModal();
				}
			}
			if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
				event.preventDefault();
				event.stopPropagation();
				const nextItemIndex = getNextItemIndex(items.length, selectedIssueIndex, event.key);
				if (nextItemIndex != null) {
					setSelectedIssueIndex(nextItemIndex);
					onClickItem(items[nextItemIndex]);
				}
			}
		},
		[
			isStreaming,
			handleOnStop,
			openDiscardSuggestionsModal,
			items,
			selectedIssueIndex,
			setSelectedIssueIndex,
			onClickItem,
		],
	);

	const fieldRef = useRef<HTMLDivElement | null>(null);
	useEffect(() => {
		const { current } = fieldRef;
		if (isStreaming && current) {
			current.focus({ preventScroll: true });
		}
	}, [isStreaming]);

	const renderItems = useMemo(
		() =>
			items.map((item, index) => {
				const { summary, issueTypeId } = item;
				const selectedIssueType = getSelectedIssueType(childIssueTypes, issueTypeId.toString());
				const statusProps = defaultStatus && {
					statusCategory: defaultStatus.name,
					statusId: defaultStatus.id,
					statusCategoryId: defaultStatus.statusCategory.id,
					statusName: defaultStatus.name,
				};
				const priorityProps = defaultPriority && {
					issuePriorityUrl: defaultPriority.iconUrl,
					issuePriorityName: defaultPriority.name,
				};
				const itemLineCard = (
					<ErrorBoundary key={index}>
						<ItemLineCard
							isSelected={selectedIssueIndex === index}
							onClick={() => {
								onClickItem(item);
								setSelectedIssueIndex(index);
							}}
							data-testid="issue-view-common-views.child-issues-panel.ai-issue-breakdown.ui.issue-breakdown-draft-list.item-line-card"
						>
							<JiraIssueLineCard
								id=""
								issueKey=""
								issueSummary={summary}
								isInlineEditEnabled={false}
								isInteractive={false}
								projectType={projectType}
								issueTypeName={selectedIssueType?.name}
								issueTypeIconUrl={selectedIssueType?.iconUrl}
								{...statusProps}
								{...priorityProps}
							/>
						</ItemLineCard>
					</ErrorBoundary>
				);

				return itemLineCard;
			}),
		[
			items,
			childIssueTypes,
			defaultStatus,
			defaultPriority,
			selectedIssueIndex,
			projectType,
			onClickItem,
			setSelectedIssueIndex,
		],
	);

	return (
		<Box ref={fieldRef} xcss={promptContainerStyles} tabIndex={-1} onKeyDown={handleKeyDown}>
			{ff('navigation-blocker-for-ai-work-breakdown_tb7cx') ? (
				<NavigationBlocker
					shouldModalBeShown={() => items.length > 0}
					customModalText={customModalMessages}
				/>
			) : null}
			<AIContainer
				shouldFitContainer
				isShadow
				spacing="none"
				isLoading={isLoading || isStreaming}
				testId="issue-view-common-views.child-issues-panel.ai-issue-breakdown.ui.issue-breakdown-draft-list.ai-container"
			>
				<Header value={headerPromptValue} issueHierarchyLevel={issueHierarchyLevel} />

				{renderItems}

				{isStreaming && <IssueBreakdownLineSkeleton />}
				{isStreaming && <SubscribeToIssueBreakdown onComplete={onStreamComplete} />}

				{!isStreaming && (
					<ActionsSection>
						<FeedbackForm />
						<Spacer />
						<DiscardSuggestions onDiscard={onDiscard} />
					</ActionsSection>
				)}

				<FooterSection>
					<AILogo size="medium" label="" />
					{isStreaming ? (
						<>
							<FooterText data-testid="issue-view-common-views.child-issues-panel.ai-issue-breakdown.ui.issue-breakdown-draft-list.issue-breakdown-footer-brand-text">
								{formatMessage(getLoadingStatusMessage(status))}
							</FooterText>
							<Spacer />
							<Button
								testId="issue-view-common-views.child-issues-panel.ai-issue-breakdown.ui.issue-breakdown-draft-list.discard-suggestions-button-suggestions-button"
								type="button"
								onClick={handleOnStop}
								interactionName="auto-generated.issue-view-common-views.child-issues-panel.ai-issue-breakdown.ui.issue-breakdown-draft-list.discard-suggestions.discard-suggestions-button-suggestions-button.button"
							>
								{formatMessage(messages.labelStop)}
								<EscText>{formatMessage(messages.labelEscNonFinal)}</EscText>
							</Button>
						</>
					) : (
						<FooterText>
							<InputPrompt
								placeholder={formatMessage(messages.footerTextDraftList)}
								onChange={onChangePrompt}
								onFocus={onFocusPrompt}
								onSubmit={onSubmit}
								isDisabled={isLoading}
							/>
						</FooterText>
					)}
				</FooterSection>
				{ff('jira-ai-work-breakdown-disclaimer-update') ? <AiFooter /> : <Footer />}
			</AIContainer>
		</Box>
	);
};

const promptContainerStyles = xcss({
	marginTop: 'space.200',
	marginBottom: 'space.200',
	border: 0,
	outline: 'none', // remove focus highlight
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ActionsSection = styled.div({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	padding: token('space.150', '12px'),
	gap: token('space.025', '2px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	borderTop: `1px solid ${token('color.background.accent.gray.subtler', colors.N300A)}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FooterText = styled.div({
	color: token('color.text.subtlest', '#626F86'),
	width: '100%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FooterSection = styled.div({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	padding: token('space.150', '12px'),
	gap: token('space.025', '2px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	borderTop: `1px solid ${token('color.background.accent.gray.subtler', colors.N40)}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Spacer = styled.div({
	flexGrow: 1,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ItemLineCard = styled.div<{
	isSelected?: boolean;
}>(
	{
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		background: token('color.background.neutral.subtle', colors.N0),
		borderRadius: '3px',
		display: 'flex',
		position: 'relative',
		padding: `0 ${token('space.200', '16px')}`,
		margin: '0',
		'&:hover': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			background: token('color.background.neutral.subtle.hovered', colors.N30),
			textDecoration: 'none',
		},
		// Extra spacing removal since this component is rendered empty,
		// https://compiledcssinjs.com/docs/api-cssmap#keys-must-be-static-strings
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		"& [data-testid='issue.issue-view.views.common.issue-line-card.issue-line-card-view.key']": {
			display: 'none',
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ isSelected = false }) =>
		isSelected
			? {
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					background: token('color.background.selected', colors.B50),
					textDecoration: 'none',
				}
			: {},
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const EscText = styled.span({
	borderRadius: token('border.radius.100', '3px'),
	padding: token('space.050', '4px'),
	color: token('color.text.subtle', '#44546F'),
	background: token('color.background.neutral', '#091E420F'),
	fontWeight: '400',
	marginLeft: token('space.075', '6px'),
});
